import React from "react";
import { Car, Copy, Check } from "lucide-react";
import { VehicleData } from "@/types";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface VehicleDataChipProps {
  vehicleData: VehicleData;
}

const VehicleDataChip: React.FC<VehicleDataChipProps> = ({ vehicleData }) => {
  const [copied, setCopied] = React.useState(false);
  const { toast } = useToast();

  const formatVehicleData = () => {
    if (vehicleData.vin) {
      return `VIN: ${vehicleData.vin}`;
    }

    if (vehicleData.year && vehicleData.make && vehicleData.model) {
      return `${vehicleData.year} ${vehicleData.make} ${vehicleData.model}`;
    }

    return vehicleData.raw || "Vehicle Information";
  };

  const handleCopy = async () => {
    try {
      const text = formatVehicleData() as string;
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast({
        description: "Vehicle information copied to clipboard",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        variant: "destructive",
        description: "Failed to copy to clipboard",
      });
    }
  };

  return (
    <div
      className="vehicle-data-chip"
      role="region"
      aria-label="Vehicle information"
    >
      <div className="vehicle-icon">
        <Car size={16} />
      </div>
      <span className="vehicle-text">{formatVehicleData() as string}</span>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleCopy}
        className="copy-button"
        aria-label="Copy vehicle information"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </Button>
    </div>
  );
};

export default VehicleDataChip;
